import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

const AuditSurveyBuilder = () => {
  const navigate = useNavigate();
  const [surveyTitle, setSurveyTitle] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedQuestions, setSelectedQuestions] = useState([]);

  const templateOptions = [
    { value: '', label: 'Start from Scratch' },
    { value: 'osha-compliance', label: 'OSHA Compliance Audit' },
    { value: 'iso-45001', label: 'ISO 45001 Assessment' },
    { value: 'workplace-safety', label: 'Workplace Safety Inspection' },
    { value: 'equipment-check', label: 'Equipment Safety Check' },
    { value: 'emergency-prep', label: 'Emergency Preparedness' },
  ];

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'workplace-safety', label: 'Workplace Safety' },
    { value: 'equipment-inspection', label: 'Equipment Inspection' },
    { value: 'emergency-procedures', label: 'Emergency Procedures' },
    { value: 'compliance-checks', label: 'Compliance Checks' },
    { value: 'ppe-requirements', label: 'PPE Requirements' },
  ];

  // Mock question bank
  const questionBank = [
    {
      id: 'q1',
      category: 'workplace-safety',
      question: 'Are all walkways and work areas clear of obstructions?',
      type: 'yes-no',
      weight: 5
    },
    {
      id: 'q2',
      category: 'workplace-safety',
      question: 'Is adequate lighting provided in all work areas?',
      type: 'yes-no',
      weight: 5
    },
    {
      id: 'q3',
      category: 'equipment-inspection',
      question: 'Are all safety guards in place and functioning properly?',
      type: 'yes-no',
      weight: 10
    },
    {
      id: 'q4',
      category: 'equipment-inspection',
      question: 'Rate the overall condition of safety equipment',
      type: 'rating',
      weight: 8
    },
    {
      id: 'q5',
      category: 'emergency-procedures',
      question: 'Are emergency exits clearly marked and accessible?',
      type: 'yes-no',
      weight: 10
    },
    {
      id: 'q6',
      category: 'emergency-procedures',
      question: 'Are fire extinguishers inspected and accessible?',
      type: 'yes-no',
      weight: 10
    },
    {
      id: 'q7',
      category: 'compliance-checks',
      question: 'Are all required safety permits up to date?',
      type: 'yes-no',
      weight: 8
    },
    {
      id: 'q8',
      category: 'ppe-requirements',
      question: 'Is appropriate PPE available and in good condition?',
      type: 'yes-no',
      weight: 10
    }
  ];

  const handleAddQuestion = (question) => {
    if (!selectedQuestions?.find(q => q?.id === question?.id)) {
      setSelectedQuestions([...selectedQuestions, question]);
    }
  };

  const handleRemoveQuestion = (questionId) => {
    setSelectedQuestions(selectedQuestions?.filter(q => q?.id !== questionId));
  };

  const handlePublish = () => {
    console.log('Publishing survey:', { surveyTitle, selectedQuestions });
    navigate('/safety-audit-dashboard');
  };

  const filteredQuestions = selectedCategory === 'all' 
    ? questionBank 
    : questionBank?.filter(q => q?.category === selectedCategory);

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Audit Survey Builder</h1>
            <p className="text-muted-foreground">
              Create customized safety audit surveys using pre-built question libraries
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/safety-audit-dashboard')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors font-medium"
            >
              <Icon name="X" size={20} />
              Cancel
            </button>
            <Button 
              onClick={handlePublish}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
            >
              <Icon name="Send" size={20} />
              Publish Survey
            </Button>
          </div>
        </div>

        {/* Survey Configuration */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Survey Configuration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Survey Title</label>
              <Input
                type="text"
                value={surveyTitle}
                onChange={(e) => setSurveyTitle(e?.target?.value)}
                placeholder="Enter survey title"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Template</label>
              <Select
                options={templateOptions}
                value={selectedTemplate}
                onChange={setSelectedTemplate}
                placeholder="Select a template"
              />
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Question Bank */}
        <div className="lg:col-span-2">
          <div className="bg-card border border-border rounded-lg">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">Question Bank</h2>
                <Select
                  options={categoryOptions}
                  value={selectedCategory}
                  onChange={setSelectedCategory}
                  placeholder="Filter by category"
                  className="w-64"
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Drag and drop questions or click the add button to build your survey
              </p>
            </div>
            <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto">
              {filteredQuestions?.map((question) => (
                <div key={question?.id} className="p-4 bg-muted border border-border rounded-lg hover:border-primary transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {question?.category?.replace('-', ' ')}
                        </span>
                        <span className="text-xs text-muted-foreground">Weight: {question?.weight}</span>
                      </div>
                      <p className="text-sm font-medium text-foreground mb-2">{question?.question}</p>
                      <p className="text-xs text-muted-foreground">Type: {question?.type}</p>
                    </div>
                    <button 
                      onClick={() => handleAddQuestion(question)}
                      disabled={selectedQuestions?.find(q => q?.id === question?.id)}
                      className="p-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Add to survey"
                    >
                      <Icon name="Plus" size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Survey Preview */}
        <div className="space-y-6">
          {/* Selected Questions */}
          <div className="bg-card border border-border rounded-lg">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Survey Preview</h3>
                <span className="text-sm text-muted-foreground">{selectedQuestions?.length} questions</span>
              </div>
            </div>
            <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto">
              {selectedQuestions?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="ClipboardList" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No questions added yet</p>
                </div>
              ) : (
                selectedQuestions?.map((question, index) => (
                  <div key={question?.id} className="p-3 bg-muted border border-border rounded-lg">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-medium text-foreground">Q{index + 1}</span>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                            {question?.category?.replace('-', ' ')}
                          </span>
                        </div>
                        <p className="text-sm text-foreground">{question?.question}</p>
                      </div>
                      <button 
                        onClick={() => handleRemoveQuestion(question?.id)}
                        className="p-1 hover:bg-destructive/10 rounded transition-colors"
                        title="Remove question"
                      >
                        <Icon name="X" size={16} color="var(--color-destructive)" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Publishing Options */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">Publishing Options</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Assign to Users</label>
                <Select
                  options={[
                    { value: 'all', label: 'All Users' },
                    { value: 'auditors', label: 'Auditors Only' },
                    { value: 'managers', label: 'Managers Only' },
                    { value: 'custom', label: 'Custom Selection' },
                  ]}
                  value="all"
                  onChange={() => {}}
                  placeholder="Select users"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Location</label>
                <Select
                  options={[
                    { value: 'all', label: 'All Locations' },
                    { value: 'plant-1', label: 'Plant 1' },
                    { value: 'plant-2', label: 'Plant 2' },
                    { value: 'plant-3', label: 'Plant 3' },
                  ]}
                  value="all"
                  onChange={() => {}}
                  placeholder="Select location"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Due Date</label>
                <Input
                  type="date"
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Scoring Configuration */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">Scoring Configuration</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="text-sm text-foreground">Total Weight</span>
                <span className="text-sm font-medium text-foreground">
                  {selectedQuestions?.reduce((sum, q) => sum + q?.weight, 0)}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="text-sm text-foreground">Pass/Fail Threshold</span>
                <span className="text-sm font-medium text-foreground">70%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditSurveyBuilder;